import React from "react";
import { Routes, Route, Navigate, Outlet } from "react-router-dom";
import { Box } from "@mui/system";
import { useAppSelector } from "src/store/app/hooks";
import MainDrawer from "src/components/common/MainDrawer";
import NoEntryPage from "src/pages/NoEntryPage";
import PageNotFound from "src/pages/PageNotFound";
import FooterPage from "src/pages/FooterPage";
import DashboardPage from "src/pages/DashboardPage";
import { SiteLogoLoader } from "src/components/common/loading/SiteLogoLoader";
import { routeLoaders } from "./routeLoaders";

const DataManagement = React.lazy(routeLoaders.dataManagement);
const Settings = React.lazy(routeLoaders.settings);
const MyInfo = React.lazy(routeLoaders.myInfo);
const CreateAccountPage = React.lazy(routeLoaders.createAccount);
const AdminPage = React.lazy(routeLoaders.admin);
const PPIInhibitorRound1 = React.lazy(routeLoaders.ppiRound1);
const PPIInhibitorRound2 = React.lazy(routeLoaders.ppiRound2);
const KinaseInhibitorRound1 = React.lazy(routeLoaders.kinaseRound1);
const KinaseInhibitorRound2 = React.lazy(routeLoaders.kinaseRound2);
const CreditChargePage = React.lazy(routeLoaders.creditCharge);
const PaymentHistoryPage = React.lazy(routeLoaders.paymentHistory);
const UserBatchManagement = React.lazy(routeLoaders.userBatchManagement);
const PreclinicalDrugResponsePage = React.lazy(routeLoaders.preclinicalDrugResponse);
const ManualPage = React.lazy(routeLoaders.manual);
const CreditListManagement = React.lazy(routeLoaders.creditListManagement);
const DashboardDetailPage = React.lazy(routeLoaders.dashboardDetail);
const ClinicalDrugResponsePage = React.lazy(routeLoaders.clinicalDrugResponse);
const DrugToxicityPage = React.lazy(routeLoaders.drugToxicity);
const CreditUserDetailListManagement = React.lazy(routeLoaders.creditUserDetail);
const PolicyPage = React.lazy(routeLoaders.policy);
const ContactUsPage = React.lazy(routeLoaders.contactUs);
const FaqPage = React.lazy(routeLoaders.faq);
const MdtrPage = React.lazy(routeLoaders.mdtr);
const AnimalToxicityPage = React.lazy(routeLoaders.animalToxicity);
const BiomarkerPage = React.lazy(routeLoaders.biomarker);

/**
 * lazy 라우트 chunk 를 기다리는 동안 표시하는 로더.
 *
 * 실제 페이지는 `<Main>`(StyledMain) 안에서 렌더되고 사이드바 폭만큼 marginLeft 를 갖는다.
 * 이 fallback 은 `<Outlet />` 바깥에 있어 그 오프셋을 물려받지 못하므로,
 * 같은 drawerWidth 를 직접 적용해 콘텐츠 영역 기준으로 중앙에 오게 한다.
 * (적용하지 않으면 사이드바 폭의 절반만큼 왼쪽으로 치우친다.)
 */
const RouteLoadingFallback = () => {
  const drawerWidth = useAppSelector((state) => state.drawer.drawerWidth);

  return (
    <Box
      sx={{
        marginLeft: `${drawerWidth}px`,
        minHeight: "60vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <SiteLogoLoader />
    </Box>
  );
};


//router
const Router = (params: { Main: React.ComponentType<any> }) => {
  const { Main } = params;
  const itemList = useAppSelector((state) => state.drawer.itemList);
  const isAdmin = useAppSelector((state) => state.checkAdmin.response?.is_admin);
  const adminElement = (children: React.ReactNode) => (
    <Main>{isAdmin ? children : <NoEntryPage />}</Main>
  );

  return (
    <Routes>
      <Route
        element={
          <>
            <MainDrawer onNextStep={undefined} />
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                minHeight: "100vh",
                backgroundColor: "#fff",
              }}
            >
              {/* <MainDrawer onNextStep={undefined} /> */}
              <Box sx={{ flex: 1, backgroundColor: "#fff" }}>
                <React.Suspense fallback={<RouteLoadingFallback />}>
                  <Outlet />
                </React.Suspense>
              </Box>
              <FooterPage />
            </Box>
          </>
        }
      >
        <Route path="/" element={<Navigate replace to="/dashboard" />}></Route>
        <Route
          path="/create-account"
          element={
            <CreateAccountPage
              onLoginSuccess={function (): void {
                throw new Error("Function not implemented.");
              }}
            />
          }
        />

        <Route
          path="/home"
          element={
            <Main>
              <DashboardPage />
            </Main>
          }
        ></Route>
        <Route
          path="/dashboard/detail"
          element={
            <Main>
              <DashboardDetailPage />
            </Main>
          }
        />
        <Route
          path="/admin-page"
          element={adminElement(<AdminPage />)}
        ></Route>
        <Route
          path="/batch-management"
          element={adminElement(<UserBatchManagement />)}
        ></Route>
        <Route
          path="/credit-management"
          element={adminElement(<CreditListManagement />)}
        ></Route>
        <Route
          path="/credit-management/user-detail/:userId"
          element={adminElement(<CreditUserDetailListManagement />)}
        ></Route>

        <Route path="/dashboard" element={<DashboardPage />} />
        <Route path="/dashboard/detail" element={<DashboardDetailPage />} />
        <Route
          path="/credit-charge-page"
          element={
            <Main>
              <CreditChargePage />
            </Main>
          }
        ></Route>
        <Route
          path="/payment-history"
          element={
            <Main>
              <PaymentHistoryPage />
            </Main>
          }
        ></Route>
        <Route
          path="/manual"
          element={
            <Main>
              <ManualPage />
            </Main>
          }
        ></Route>
        <Route
          path="/terms-of-service"
          element={
            <Main>
              <PolicyPage variant="terms" />
            </Main>
          }
        />
        <Route
          path="/privacy-policy"
          element={
            <Main>
              <PolicyPage variant="privacy" />
            </Main>
          }
        />
        <Route
          path="/contact-us"
          element={
            <Main>
              <ContactUsPage />
            </Main>
          }
        />
        <Route
          path="/faq"
          element={
            <Main>
              <FaqPage />
            </Main>
          }
        />
        <Route
          path="/drug-response/preclinical/:libraryName/round1"
          element={
            <Main>
              <PreclinicalDrugResponsePage />
            </Main>
          }
        ></Route>
        <Route
          path="/drug-response/preclinical/:libraryName/round1/clinicalAssistant"
          element={
            <Main>
              <PreclinicalDrugResponsePage />
            </Main>
          }
        ></Route>
        <Route
          path="/drug-response/clinical/:libraryName/round1"
          element={
            <Main>
              <ClinicalDrugResponsePage />
            </Main>
          }
        ></Route>
        <Route
          path="/drug-response/clinical/:libraryName/round1/clinicalAssistant"
          element={
            <Main>
              <ClinicalDrugResponsePage />
            </Main>
          }
        ></Route>
        <Route
          path="/biomarker/:libraryName/round1"
          element={
            <Main>
              <BiomarkerPage />
            </Main>
          }
        ></Route>
        <Route
          path="/drug-toxicity/fatetox/:libraryName/round1"
          element={
            <Main>
              <DrugToxicityPage />
            </Main>
          }
        ></Route>
        <Route
          path="/drug-toxicity/mdtr/:libraryName/round1"
          element={
            <Main>
              <MdtrPage />
            </Main>
          }
        ></Route>
        <Route
          path="/drug-toxicity/animal/:libraryName/round1"
          element={
            <Main>
              <AnimalToxicityPage />
            </Main>
          }
        ></Route>
        <Route
          path="/drug-toxicity/:libraryName/round1"
          element={
            <Main>
              <DrugToxicityPage />
            </Main>
          }
        ></Route>
        <Route
          path="/ppi-inhibitor/:libraryName/round1"
          element={
            <Main>
              <PPIInhibitorRound1 />
            </Main>
          }
        ></Route>
        <Route
          path="/ppi-inhibitor/:libraryName/:roundId"
          element={
            <Main>
              <PPIInhibitorRound2 />
            </Main>
          }
        ></Route>
        <Route
          path="/kinase-inhibitor/:libraryName/round1"
          element={
            <Main>
              <KinaseInhibitorRound1 />
            </Main>
          }
        ></Route>
        <Route
          path="/kinase-inhibitor/:libraryName/:roundId"
          element={
            <Main>
              <KinaseInhibitorRound2 />
            </Main>
          }
        />
        <Route
          path="/mdtr/:libraryName/:roundId"
          element = {
            <Main>
              <MdtrPage/>
            </Main>
          }
        >
          
        </Route>
        <Route
          path="/datamanagement"
          element={
            <Main>
              <DataManagement />
            </Main>
          }
        ></Route>
        {/* <Route
          path="/prompt"
          <prompt />
        ></Route> */}
        <Route
          path="/settings"
          element={
            <Main>
              <Settings />
            </Main>
          }
        ></Route>
        <Route
          path="/myinfo"
          element={
            <Main>
              <MyInfo />
            </Main>
          }
        ></Route>
        <Route path="/login" element={<Navigate replace to="/dashboard" />} />
        <Route
          path="/create-account"
          element={
            <CreateAccountPage
              onLoginSuccess={function (): void {
                throw new Error("Function not implemented.");
              }}
            />
          }
        />
        {itemList == null ? (
          <></>
        ) : (
          itemList.map((item, idx) => {
            return (
              <Route
                path={`/prompt/${item}`}
                key={idx}
                element={<Main>{/* <Prompt /> */}</Main>}
              ></Route>
            );
          })
        )}
      </Route>
      <Route path="*" element={<PageNotFound />} />
    </Routes>
  );
};

export default Router;
